/* Finds every colour of a design with one small search request per design and fills the
   colour swatches on the product page and the colour dots on product cards.
   The server already renders the colours it can see, this makes the list complete. */
(() => {
  const SEP = ' - ';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const isAr = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  const TTL = 10 * 60 * 1000;
  const pending = new Map();
  let queue = [];
  let active = 0;
  const MAX_PARALLEL = 4;

  const baseOf = (title) => {
    const i = title.lastIndexOf(SEP);
    return i < 0 ? null : title.slice(0, i);
  };
  const colourOf = (title) => {
    const i = title.lastIndexOf(SEP);
    return i < 0 ? '' : title.slice(i + SEP.length);
  };

  const readCache = (base) => {
    try {
      const raw = sessionStorage.getItem('px-sib:' + base);
      if (!raw) return null;
      const data = JSON.parse(raw);
      return Date.now() - data.t < TTL ? data.v : null;
    } catch (e) {
      return null;
    }
  };
  const writeCache = (base, v) => {
    try {
      sessionStorage.setItem('px-sib:' + base, JSON.stringify({ t: Date.now(), v }));
    } catch (e) {}
  };

  const runQueue = () => {
    while (active < MAX_PARALLEL && queue.length) {
      const job = queue.shift();
      active++;
      const url = `${root}search?view=px-siblings&type=product&q=${encodeURIComponent('"' + job.base + '"')}`;
      fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : '[]'))
        .then((txt) => {
          let list = [];
          try {
            list = JSON.parse(txt);
          } catch (e) {}
          list = list.filter((p) => baseOf(p.title) === job.base);
          writeCache(job.base, list);
          job.resolve(list);
        })
        .catch(() => job.resolve([]))
        .finally(() => {
          active--;
          runQueue();
        });
    }
  };

  const getSiblings = (base) => {
    const cached = readCache(base);
    if (cached) return Promise.resolve(cached);
    if (pending.has(base)) return pending.get(base);
    const p = new Promise((resolve) => {
      queue.push({ base, resolve });
      runQueue();
    });
    pending.set(base, p);
    return p;
  };

  const withCurrentFirst = (list, handle) => {
    const cur = list.filter((p) => p.handle === handle);
    const rest = list.filter((p) => p.handle !== handle).sort((a, b) => colourOf(a.title).localeCompare(colourOf(b.title)));
    return cur.concat(rest);
  };

  const renderDots = (el, list) => {
    const handle = el.dataset.pxHandle;
    if (list.length < 2) return;
    const ordered = withCurrentFirst(list, handle);
    const MAX = 6;
    el.textContent = '';
    ordered.slice(0, MAX).forEach((p) => {
      if (!p.hex) return;
      const dot = document.createElement('span');
      dot.className = 'px-dot' + (p.handle === handle ? ' is-current' : '');
      dot.style.setProperty('--px-dot', p.hex);
      if (p.handle !== handle) dot.title = colourOf(p.title);
      el.appendChild(dot);
    });
    if (ordered.length > MAX) {
      const more = document.createElement('span');
      more.className = 'px-card-dots__more';
      more.textContent = '+' + (ordered.length - MAX);
      el.appendChild(more);
    }
    el.setAttribute('aria-label', ordered.length + (isAr ? ' ألوان' : ' colours'));
    el.hidden = false;
  };

  const renderSwatches = (el, list) => {
    const handle = el.dataset.pxHandle;
    if (list.length < 2) return;
    const ordered = withCurrentFirst(list, handle);
    const count = el.querySelector('.px-siblings__count');
    if (count) count.textContent = ordered.length + (isAr ? ' ألوان' : ' colours');
    const ul = el.querySelector('.px-siblings__list');
    if (!ul) return;
    ul.textContent = '';
    ordered.forEach((p) => {
      const name = colourOf(p.title);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = p.url;
      a.title = name;
      a.setAttribute('aria-label', name + (p.available ? '' : isAr ? '، نفدت الكمية' : ', sold out'));
      a.className = 'px-swatch' + (p.handle === handle ? ' is-current' : '') + (p.available ? '' : ' is-soldout') + (p.hex ? '' : ' px-swatch--text');
      if (p.handle === handle) a.setAttribute('aria-current', 'true');
      if (p.hex) {
        const dot = document.createElement('span');
        dot.className = 'px-swatch__dot';
        dot.style.setProperty('--px-dot', p.hex);
        a.appendChild(dot);
      } else {
        const n = document.createElement('span');
        n.className = 'px-swatch__name';
        n.textContent = name;
        a.appendChild(n);
      }
      li.appendChild(a);
      ul.appendChild(li);
    });
    el.hidden = false;
    if (el.dataset.pxSingle === 'true') document.documentElement.classList.add('px-siblings-single');
  };

  const scan = () => {
    document.querySelectorAll('[data-px-base]:not([data-px-done])').forEach((el) => {
      el.dataset.pxDone = '1';
      const base = el.dataset.pxBase;
      if (!base) return;
      getSiblings(base).then((list) => {
        if (!el.isConnected) return;
        if (el.classList.contains('px-siblings')) renderSwatches(el, list);
        else renderDots(el, list);
      });
    });
  };

  let t;
  const schedule = () => {
    clearTimeout(t);
    t = setTimeout(scan, 60);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  new MutationObserver(schedule).observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
